import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@paypalcorp/pp-react-buttons';
import { change, Field } from 'redux-form';
import getContent from 'pp-react-l10n';
import { renderSwitchComponent } from '../switchPanels/SwitchPanel';
import LogoModal from './LogoModal';
import _isEmpty from 'lodash/isEmpty';
import { EditIcon } from '@paypalcorp/pp-react-icons';
import { ContextualAlert } from '@paypalcorp/pp-react-contextual-alert';
import {
  LOGO_TARGET_BUTTON,
  LOGO_TARGET_NOLOGO,
  LOGO_TARGET_PROFILE,
} from '../../reducers/reducerButtonFactoryV3';
import { BTN_FACTORY_V3_FORM } from '../../clientConstants';
import { trackEventWithData } from '../../utilsV3/analytics';
import { FPTI_SPEC } from '../../utilsV3/fptiConstants';

const getImageBannerContent = getContent('locals')('buttonsV3/logoExperience');
const title = getImageBannerContent('switchTitle');
const button = getImageBannerContent('uploadButton');
const editButton = getImageBannerContent('editButton');

const LogoExperience = ({
  isChecked,
  uploadImage,
  updateBusinessProfileLogo,
  updateBusinessImageUrls,
  logoUrl,
  logoTarget,
  businessProfileLogoUrl,
  dispatch,
  logoClientError,
  noLogoImage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [logoImg, setLogoImg] = useState();
  const [isLogoBusinessProfile, setIsLogoBusinessProfile] = useState(false);

  useEffect(() => {
    // In case user has logo in business profile
    // once user toggle on the logo option
    // we should display their business profile logo
    // and update logo target to be PROFILE
    if (
      noLogoImage === 'no' &&
      logoTarget === LOGO_TARGET_NOLOGO &&
      !_isEmpty(businessProfileLogoUrl)
    ) {
      dispatch(change(BTN_FACTORY_V3_FORM, 'logoTarget', LOGO_TARGET_PROFILE));
      setIsLogoBusinessProfile(true);
    }
  }, [noLogoImage]);

  useEffect(() => {
    switch (logoTarget) {
      case LOGO_TARGET_PROFILE:
        setLogoImg(businessProfileLogoUrl);
        break;
      case LOGO_TARGET_BUTTON:
        setLogoImg(logoUrl);
        break;
      case LOGO_TARGET_NOLOGO:
      default:
        setLogoImg('');
        break;
    }
  }, [logoTarget]);

  useEffect(() => {
    setIsLogoBusinessProfile(
      !_isEmpty(businessProfileLogoUrl) &&
        logoTarget === LOGO_TARGET_PROFILE &&
        logoImg === businessProfileLogoUrl
    );
  }, []);

  const logoImageContainer = () => {
    if (_isEmpty(logoImg)) {
      return (
        <>
          <Button
            type="Button"
            className="upload-button"
            onClick={() => setIsOpen(true)}
            secondary
          >
            {button}
          </Button>
          {logoClientError && (
            <ContextualAlert type="error">{logoClientError}</ContextualAlert>
          )}
        </>
      );
    }
    return (
      <div className="logo-img-container">
        <img className="logo-img" src={logoImg} />
        <Button
          className="button-link"
          type="button"
          onClick={() => {
            setIsOpen(true);
            trackEventWithData(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_UPLOAD_LOGO_CL);
          }}
          iconComponent={EditIcon}
          tertiary
        >
          {editButton}
        </Button>
      </div>
    );
  };

  return (
    <div className="logo-switch-container">
      <Field
        id="getLogoImage"
        name="noLogoImage"
        label="getLogoImage"
        title={title}
        component={renderSwitchComponent}
        isChecked={isChecked}
        reversed={true}
      />

      {isChecked && logoImageContainer()}

      <LogoModal
        getContent={getImageBannerContent}
        isOpen={isOpen}
        handleCloseModal={() => setIsOpen(false)}
        uploadImage={uploadImage}
        updateBusinessProfileLogo={updateBusinessProfileLogo}
        updateBusinessImageUrls={updateBusinessImageUrls}
        logoUrl={logoImg}
        setLogoImg={setLogoImg}
        logoTarget={logoTarget}
        isLogoBusinessProfile={isLogoBusinessProfile}
        setIsLogoBusinessProfile={setIsLogoBusinessProfile}
        dispatch={dispatch}
      />
    </div>
  );
};

LogoExperience.propTypes = {
  isChecked: PropTypes.bool,
  uploadImage: PropTypes.func,
  updateBusinessProfileLogo: PropTypes.func,
  updateBusinessImageUrls: PropTypes.func,
  logoUrl: PropTypes.string,
  logoTarget: PropTypes.string,
  businessProfileLogoUrl: PropTypes.string,
  dispatch: PropTypes.func,
  logoClientError: PropTypes.string,
  noLogoImage: PropTypes.string,
};

export default LogoExperience;
